import { Howler } from 'howler';
import { settings } from '@/config';

/**
 * Premium stone-interaction audio for the ORVAE entrance portal (2026-07-18, from the user's
 * "premium stone interaction audio system" brief).
 *
 * SAMPLE-FIRST, synth-fallback. The user supplied one handcrafted recording per family in
 * `public/fx/Stones/` (Heavy Top / Wall / Arch / Decorative Column / Keystone) plus a shared
 * "Stone Return". Those play on the matching event. If a file ever fails to load, the family
 * falls back to a procedural Web Audio voice ({@link FAMILY_VOICES}) so the doorway is never
 * silent — that's the "procedural now, recordings later" path the user chose, with the
 * recordings now in place.
 *
 * Everything runs on Howler's own AudioContext + masterGain, so master volume / mute / blur-mute
 * apply to both sample and synth voices.
 *
 * With only ONE recording per family, the brief's anti-repetition trick is essential and lives
 * here: every play gets a micro-variation — pitch ±`pitchJitter`, volume ±`volumeJitter`, a
 * 5-15ms start delay — so the same file never reads as a looped UI sample. Two event kinds:
 * `'lift'` as the stone floats out (loudness scaled by cursor speed) and `'settle'` as it
 * returns (a fixed gentle set-down = the shared "Stone Return").
 */
export type StoneFamily = 'A' | 'B' | 'C' | 'D' | 'E';
export type StoneSoundKind = 'lift' | 'settle';

/** Synth fallback voice — only used if a family's recording fails to load. Character mirrors the
 *  brief (A heavy/deep → E warm; D small/quiet): soft filtered noise over a low damped resonance. */
interface FamilyVoice {
  fundamental: number;
  bodyLevel: number;
  bodyDecay: number;
  frictionHz: number;
  frictionQ: number;
  frictionLevel: number;
  frictionDecay: number;
  attack: number;
  echoDelay: number;
  echoMix: number;
}
const FAMILY_VOICES: Record<StoneFamily, FamilyVoice> = {
  A: { fundamental: 54, bodyLevel: 0.5, bodyDecay: 0.62, frictionHz: 190, frictionQ: 1.1, frictionLevel: 0.16, frictionDecay: 0.5, attack: 0.045, echoDelay: 0, echoMix: 0 },
  B: { fundamental: 92, bodyLevel: 0.34, bodyDecay: 0.34, frictionHz: 330, frictionQ: 1.4, frictionLevel: 0.2, frictionDecay: 0.32, attack: 0.026, echoDelay: 0, echoMix: 0 },
  C: { fundamental: 80, bodyLevel: 0.36, bodyDecay: 0.44, frictionHz: 270, frictionQ: 0.9, frictionLevel: 0.15, frictionDecay: 0.4, attack: 0.032, echoDelay: 0.045, echoMix: 0.22 },
  D: { fundamental: 168, bodyLevel: 0.22, bodyDecay: 0.22, frictionHz: 460, frictionQ: 1.6, frictionLevel: 0.16, frictionDecay: 0.2, attack: 0.016, echoDelay: 0, echoMix: 0 },
  E: { fundamental: 64, bodyLevel: 0.55, bodyDecay: 0.72, frictionHz: 210, frictionQ: 1.0, frictionLevel: 0.13, frictionDecay: 0.52, attack: 0.05, echoDelay: 0.06, echoMix: 0.16 },
};

/** Buffer-map key: a family letter for its lift recording, or 'return' for the settle one. */
type BufferKey = StoneFamily | 'return';

export class StoneAudio {
  private ctx: AudioContext | null = null;
  private out: AudioNode | null = null;
  /** All voices route through this shared EQ (bass low-shelf → high-cut) before `out`, so the
   *  whole stone set gets its low-end weight from one place. */
  private voiceOut: AudioNode | null = null;
  private bass: BiquadFilterNode | null = null;
  private lowpass: BiquadFilterNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private readonly buffers = new Map<BufferKey, AudioBuffer>();
  private loadStarted = false;

  /** Grab Howler's context/master lazily (Howler only creates them once audio unlocks), build
   *  the shared EQ, and kick off sample decoding. Returns true once the context exists. */
  private ensure(): boolean {
    if (!this.ctx) {
      const ctx = Howler.ctx as AudioContext | undefined;
      if (!ctx) return false;
      this.ctx = ctx;
      this.out = (Howler.masterGain as GainNode | undefined) ?? ctx.destination;

      // Shared tone chain: voices → bass low-shelf → high-cut → master. One place for the
      // "more bass / less harsh" the user asked for; params pushed from settings each play.
      this.bass = ctx.createBiquadFilter();
      this.bass.type = 'lowshelf';
      this.lowpass = ctx.createBiquadFilter();
      this.lowpass.type = 'lowpass';
      this.bass.connect(this.lowpass).connect(this.out);
      this.voiceOut = this.bass;

      this.buildNoise(ctx);
    }
    this.applyTone();
    this.loadSamples();
    return true;
  }

  /** Push the live bass / high-cut settings onto the shared EQ (called each play, so GUI edits
   *  apply on the next hit). */
  private applyTone(): void {
    const cfg = settings.audio.stoneFx;
    if (this.bass) {
      this.bass.frequency.value = cfg.bassFreq;
      this.bass.gain.value = cfg.bassGain;
    }
    if (this.lowpass) this.lowpass.frequency.value = cfg.lowpass;
  }

  /** One reusable dark noise buffer (white → leaky-integrated toward the dull porous texture of
   *  limestone), sampled at a random offset per synth play so the grain never repeats. */
  private buildNoise(ctx: AudioContext): void {
    const len = Math.floor(ctx.sampleRate * 1.5);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i += 1) {
      last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02;
      data[i] = last * 3.2;
    }
    this.noiseBuf = buf;
  }

  /** Fetch + decode each family recording once. decodeAudioData works on a suspended context, so
   *  this can run before the first gesture; a family whose fetch/decode fails just stays on synth. */
  private loadSamples(): void {
    if (this.loadStarted || !this.ctx) return;
    this.loadStarted = true;
    const ctx = this.ctx;
    const fam = settings.audio.stoneFx.families;
    const entries: Array<[BufferKey, string]> = [
      ['A', fam.A.sample],
      ['B', fam.B.sample],
      ['C', fam.C.sample],
      ['D', fam.D.sample],
      ['E', fam.E.sample],
      ['return', settings.audio.stoneFx.return.sample],
    ];
    for (const [key, path] of entries) {
      if (!path) continue;
      // encodeURI keeps the parens but escapes the spaces in the filenames.
      fetch(encodeURI(path))
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((ab) => ctx.decodeAudioData(ab))
        .then((buf) => this.buffers.set(key, buf))
        .catch(() => {
          /* stay on the synth fallback for this key */
        });
    }
  }

  /**
   * Play one stone sound. `intensity` (0..1) is the cursor-speed loudness for a lift; settles use
   * the fixed `settleLevel`. Applies per-play pitch/volume/delay micro-variation.
   */
  play(family: StoneFamily, kind: StoneSoundKind, intensity: number): void {
    if (!settings.audio.enabled || !this.ensure()) return;
    const ctx = this.ctx!;
    if (ctx.state === 'suspended') void ctx.resume(); // safe after any user gesture

    const cfg = settings.audio.stoneFx;

    // Per-play micro-variation (brief: pitch ±3-5%, volume ±2-4%, 5-15ms delay).
    const rand = (spread: number) => 1 + (Math.random() * 2 - 1) * spread;
    const pitchJit = rand(cfg.pitchJitter);
    const volJit = rand(cfg.volumeJitter);
    const delay = (cfg.delayMinMs + Math.random() * (cfg.delayMaxMs - cfg.delayMinMs)) / 1000;
    const t0 = ctx.currentTime + delay;

    // Loudness: lift scales with cursor speed (soft→loud); settle is a fixed gentle set-down.
    const loud =
      kind === 'lift'
        ? cfg.minVolume + (cfg.maxVolume - cfg.minVolume) * clamp01(intensity)
        : cfg.settleLevel;

    // Per-family / return base trims (the user can balance A..E + return independently).
    const trim = kind === 'settle' ? cfg.return : cfg.families[family];
    const gain = cfg.masterGain * loud * volJit * trim.volume;
    if (gain <= 0) return;
    // Global pitch multiplier (user's "make low pitch") on top of the family's own rate.
    const pitch = pitchJit * trim.rate * cfg.pitch;

    const key: BufferKey = kind === 'settle' ? 'return' : family;
    // The return eases off with a smooth fade-out; lifts play out naturally (fade 0).
    const fadeOut = kind === 'settle' ? settings.audio.stoneFx.return.fadeOut : 0;
    const buffer = this.buffers.get(key);
    if (buffer) this.sampleVoice(ctx, buffer, t0, pitch, gain, fadeOut);
    else this.synthVoice(ctx, family, kind, t0, pitch, gain); // fallback until/if the file loads
  }

  /** Recording playback: one-shot buffer source, `playbackRate` = pitch/speed, through a gain
   *  node. `fadeOut` (secs) applies a smooth exponential ease-off over the tail — used by the
   *  return so it settles gently and rings a touch past the stones reaching home. */
  private sampleVoice(
    ctx: AudioContext,
    buffer: AudioBuffer,
    t0: number,
    pitch: number,
    gain: number,
    fadeOut = 0,
  ): void {
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = pitch;
    const g = ctx.createGain();
    const playDur = buffer.duration / pitch; // faster playback = shorter clip

    if (fadeOut > 0) {
      const fade = Math.min(fadeOut, playDur);
      const fadeStart = t0 + (playDur - fade);
      g.gain.setValueAtTime(gain, t0);
      g.gain.setValueAtTime(gain, fadeStart);
      g.gain.exponentialRampToValueAtTime(Math.max(gain * 0.001, 1e-4), fadeStart + fade);
      src.connect(g).connect(this.voiceOut!);
      src.start(t0);
      src.stop(t0 + playDur + 0.02);
    } else {
      g.gain.value = gain;
      src.connect(g).connect(this.voiceOut!);
      src.start(t0);
    }
  }

  // ---- Synth fallback (only runs for a family whose recording didn't load) ----------------

  private synthVoice(
    ctx: AudioContext,
    family: StoneFamily,
    kind: StoneSoundKind,
    t0: number,
    pitch: number,
    gain: number,
  ): void {
    const v = FAMILY_VOICES[family];
    const kindPitch = kind === 'settle' ? 0.95 : 1;
    const decayScale = kind === 'settle' ? 0.7 : 1;
    this.body(ctx, v, family, t0, v.fundamental * pitch * kindPitch, gain, v.bodyDecay * decayScale);
    this.friction(ctx, v, t0, pitch, gain * (kind === 'settle' ? 0.55 : 1), v.frictionDecay * decayScale);
  }

  private body(ctx: AudioContext, v: FamilyVoice, family: StoneFamily, t0: number, freq: number, gain: number, decay: number): void {
    const g = ctx.createGain();
    const peak = Math.max(gain * v.bodyLevel, 1e-4);
    g.gain.setValueAtTime(1e-4, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + v.attack);
    g.gain.exponentialRampToValueAtTime(1e-4, t0 + decay);
    g.connect(this.voiceOut!);
    if (v.echoMix > 0) {
      const d = ctx.createDelay();
      d.delayTime.value = v.echoDelay;
      const dg = ctx.createGain();
      dg.gain.value = v.echoMix;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 900;
      g.connect(d).connect(lp).connect(dg).connect(this.voiceOut!);
    }
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t0);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.985, t0 + decay);
    osc.connect(g);
    osc.start(t0);
    osc.stop(t0 + decay + 0.05);
    if (family === 'E') {
      const h = ctx.createOscillator();
      h.type = 'sine';
      h.frequency.setValueAtTime(freq * 2, t0);
      const hg = ctx.createGain();
      hg.gain.value = 0.28;
      h.connect(hg).connect(g);
      h.start(t0);
      h.stop(t0 + decay + 0.05);
    }
  }

  private friction(ctx: AudioContext, v: FamilyVoice, t0: number, pitch: number, gain: number, decay: number): void {
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuf!;
    src.playbackRate.value = pitch;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = v.frictionHz * pitch;
    bp.Q.value = v.frictionQ;
    const g = ctx.createGain();
    const peak = Math.max(gain * v.frictionLevel, 1e-4);
    g.gain.setValueAtTime(1e-4, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + v.attack * 1.3);
    g.gain.exponentialRampToValueAtTime(1e-4, t0 + decay);
    src.connect(bp).connect(g).connect(this.voiceOut!);
    const maxOff = Math.max(0, this.noiseBuf!.duration - decay - 0.06);
    src.start(t0, Math.random() * maxOff, decay + 0.06);
  }

  dispose(): void {
    this.ctx = null;
    this.out = null;
    this.noiseBuf = null;
    this.buffers.clear();
  }
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
